import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  Liquor, 
  SportsBar, 
  Schedule, 
  Receipt, 
  Description 
} from '@mui/icons-material';
import PublicLayout from "../../layouts/PublicLayout";

export default function BarBrewery() {
  return (
    <PublicLayout>
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: '#f6521a', fontWeight: 800, letterSpacing: 1.5 }}>
                TPOS FOR BARS, PUBS & CRAFT BREWERIES
              </Typography>
              
              <Typography variant="h2" sx={{ fontWeight: 600, mt: 1, mb: 2, fontSize: { xs: '1.5rem', md: '2.5rem' }, lineHeight: 1.1 }}>
                Pour More Profit with Milliliter-Level <span style={{ color: "#f6521a" }}>Spirit and Keg Control.</span>
              </Typography>

              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.5 }}>
                Manage complex liquor taxes, automated happy hour shifts, and prevent bar shrinkage with real-time inventory auditing.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.primary', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                Running a bar or brewery involves high stakes and tight regulations. Whether you are tracking the remaining liters in a craft beer keg or auditing expensive single-malt pours, precision is your best friend. 
                <strong> TPOS is built to handle the unique workflows of the alcobev industry</strong>—from managing open tabs and "Happy Hour" price flips to generating state-mandated excise reports with a single click. We help you eliminate unauthorized pours and ensure every drop contributes to your bottom line.
              </Typography>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Specialized TPOS Features:
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    title: "ML-Level Inventory Tracking",
                    desc: "Track spirits by the milliliter. Selling a 'Large Peg' automatically deducts 60ml from the specific bottle's digital stock.",
                    icon: <Liquor sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Keg & Tap Management", 
                    desc: "Monitor the health of your brewery. Track keg connections, volume dispensed, and 'keg-change' alerts.",
                    icon: <SportsBar sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Automated Happy Hour Transitions", 
                    desc: "Schedule your discounts. TPOS automatically flips menu prices at 6:00 PM and reverts them at 9:00 PM seamlessly.",
                    icon: <Schedule sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Tab & Bottle Management", 
                    desc: "Keep guest tabs open throughout the night. Track 'Bottle Keeping' service for their next visit.",
                    icon: <Receipt sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Excise & Compliance Reporting", 
                    desc: "Export state-specific liquor sales reports and VAT breakdowns, significantly reducing your accountant's manual workload.",
                    icon: <Description sx={{ color: '#f6521a' }} />
                  }
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={5} sx={{ position: 'sticky', top: 100 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden', 
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 550 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'right',
                    float:'right'                    
                  }}
                >
                  <img 
                    src="/img/bar.png" 
                    alt="Bar and Brewery POS System" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                
                <Box sx={{ p: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Business Impact</Typography>
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Reduce bar shrinkage by 25-30% through strict inventory-to-sales reconciliation." />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Seamlessly manage high-volume nights with a POS that handles open tabs without lag." />
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </PublicLayout>
  );
}